import React, { useEffect, useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalTitle,
  MDBModalBody,
  MDBModalFooter,
  MDBBtn,
  MDBCheckbox,
} from 'mdb-react-ui-kit';
import { useMessageBox } from '../Common/MessageBox';
import config from '../../config';

const MONTHLY_ADDON = 15;
const UNLIMITED_MONTHLY = 35;

const ADDON_META = {
  smartscan: { id: 'smartscan', label: 'SmartScan', blurb: 'Product intelligence' },
  smartlist: { id: 'smartlist', label: 'SmartList', blurb: 'Auto cross-listing' },
  pos: { id: 'pos', label: 'POS', blurb: 'Point of sale' },
};

function SubscriptionPlansModal({
  show,
  context,
  userEmail,
  userName,
  signupFeature,
  authToken,
  checkoutSessionUrl,
  onChooseFree,
}) {
  const [plan, setPlan] = useState('free');
  const [addons, setAddons] = useState({
    smartscan: false,
    smartlist: false,
    pos: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const { showError, showWarning, MessageBoxComponent } = useMessageBox();

  useEffect(() => {
    if (!show) return;
    setPlan('free');
    setSubmitting(false);
    const next = { smartscan: false, smartlist: false, pos: false };
    if (signupFeature === 'smartscan') next.smartscan = true;
    if (signupFeature === 'smartlist') next.smartlist = true;
    if (signupFeature === 'all_in_one') {
      next.smartscan = true;
      next.smartlist = true;
      next.pos = true;
    }
    setAddons(next);
  }, [show, signupFeature]);

  const customizeTotal = useMemo(() => {
    const count = Object.values(addons).filter(Boolean).length;
    return count * MONTHLY_ADDON;
  }, [addons]);

  const paidPrice = useMemo(() => {
    if (plan === 'unlimited') return UNLIMITED_MONTHLY;
    if (plan === 'customize') return customizeTotal;
    return 0;
  }, [plan, customizeTotal]);

  const planLabel = useMemo(() => {
    if (plan === 'unlimited') return 'Unlimited';
    if (plan === 'customize') {
      const selected = Object.entries(addons)
        .filter(([, v]) => v)
        .map(([k]) => ADDON_META[k]?.label || k);
      return selected.length ? `Customize (${selected.join(', ')})` : 'Customize';
    }
    return 'Free';
  }, [plan, addons]);

  /** After payment Stripe redirects here (production: https://app.thriftops.com/). */
  const successUrl = useMemo(() => {
    const base = config.APP_BASE_URL;
    return `${base}/?checkout=success&session_id={CHECKOUT_SESSION_ID}`;
  }, []);

  const cancelUrl = useMemo(() => {
    const base = config.APP_BASE_URL;
    if (context === 'google_signup') {
      return `${base}/dashboard?checkout=cancel`;
    }
    return `${base}/register?checkout=cancel`;
  }, [context]);

  const handleContinue = async () => {
    if (plan === 'free') {
      onChooseFree();
      return;
    }

    if (plan === 'customize') {
      const selected = Object.entries(addons)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (!selected.length) {
        showWarning('Pick at least one add-on for the Customize plan, or choose Free / Unlimited.');
        return;
      }
    }

    if (!checkoutSessionUrl) {
      showError('Billing is not configured. Set REACT_APP_STRIPE_CHECKOUT_SESSION_URL or add the API route on your backend.');
      return;
    }

    const selectedAddons =
      plan === 'unlimited'
        ? ['smartscan', 'smartlist', 'pos']
        : Object.entries(addons)
            .filter(([, v]) => v)
            .map(([k]) => k);

    /** Same shape as Next.js POST: price, email, plan, testMode (+ URLs & metadata). */
    const body = {
      price: paidPrice,
      email: userEmail,
      plan: planLabel,
      testMode: config.STRIPE_TEST_MODE,
      success_url: successUrl,
      cancel_url: cancelUrl,
      addons: selectedAddons,
      signup_context: context || undefined,
      signup_feature: signupFeature || undefined,
      name: userName || undefined,
    };

    try {
      setSubmitting(true);
      const headers = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers.Authorization = `Bearer ${authToken}`;
      }

      const res = await fetch(checkoutSessionUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || data.success === false) {
        showError(
          data.message || 'Could not start checkout. Ensure the Stripe session endpoint is implemented.'
        );
        return;
      }

      /** Prefer hosted Checkout URL from the API (same as Next.js session.url) — opens Stripe in the browser. */
      const redirectUrl = data.url || data.sessionUrl;
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return;
      }

      const sessionId = data.sessionId || data.id;
      if (sessionId && config.STRIPE_PUBLISHABLE_KEY) {
        const stripe = await loadStripe(config.STRIPE_PUBLISHABLE_KEY);
        if (!stripe) {
          showError('Stripe.js failed to load.');
          return;
        }
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          showError(error.message || 'Could not redirect to Stripe Checkout.');
        }
        return;
      }

      if (sessionId && !config.STRIPE_PUBLISHABLE_KEY) {
        showError(
          'Your API should return url from Stripe, or set REACT_APP_STRIPE_PUBLISHABLE_KEY to use sessionId.'
        );
        return;
      }

      showError(
        'Checkout response missing url or sessionId. Return { url, sessionId } from stripe.checkout.sessions.create().'
      );
    } catch (e) {
      console.error(e);
      showError('Could not reach the billing server. Try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <MDBModal open={show} staticBackdrop tabIndex="-1">
        <MDBModalDialog centered size="lg" scrollable>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>Choose your plan</MDBModalTitle>
            </MDBModalHeader>
            <MDBModalBody>
              <p className="text-muted small mb-3">
                Pick Free or a paid feature plan. For paid plans, click Next to open secure Stripe checkout.
              </p>

              <div className="d-flex flex-column gap-3">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setPlan('free')}
                  onKeyDown={(e) => e.key === 'Enter' && setPlan('free')}
                  className={`border rounded p-3 ${plan === 'free' ? 'border-primary border-2' : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">Free</h6>
                      <p className="text-muted small mb-0">
                        Core access with limits. Support: email only, Tuesday–Friday during business hours.
                      </p>
                    </div>
                    <span className="fw-bold">$0</span>
                  </div>
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setPlan('customize')}
                  onKeyDown={(e) => e.key === 'Enter' && setPlan('customize')}
                  className={`border rounded p-3 ${plan === 'customize' ? 'border-primary border-2' : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="mb-1">Customize</h6>
                      <p className="text-muted small mb-0">
                        Pick any combination. Each module is ${MONTHLY_ADDON}/month. Support: email only,
                        Tuesday–Friday during business hours.
                      </p>
                    </div>
                    <span className="fw-bold">${customizeTotal}/mo</span>
                  </div>
                  {plan === 'customize' && (
                    <div className="pt-2 border-top">
                      {Object.entries(ADDON_META).map(([key, meta]) => (
                        <MDBCheckbox
                          key={key}
                          id={`addon-${key}`}
                          label={`${meta.label} — $${MONTHLY_ADDON}/mo (${meta.blurb})`}
                          checked={addons[key]}
                          onChange={(e) =>
                            setAddons((prev) => ({ ...prev, [key]: e.target.checked }))
                          }
                          className="mb-2"
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setPlan('unlimited')}
                  onKeyDown={(e) => e.key === 'Enter' && setPlan('unlimited')}
                  className={`border rounded p-3 ${plan === 'unlimited' ? 'border-primary border-2' : ''}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">Unlimited</h6>
                      <p className="text-muted small mb-0">
                        SmartScan, SmartList, and POS included. Technical support included.
                      </p>
                    </div>
                    <span className="fw-bold">${UNLIMITED_MONTHLY}/mo</span>
                  </div>
                </div>
              </div>
            </MDBModalBody>
            <MDBModalFooter className="d-flex flex-column gap-2">
              <MDBBtn color="primary" className="w-100" onClick={handleContinue} disabled={submitting}>
                {submitting
                  ? 'Opening Stripe…'
                  : plan === 'free'
                    ? 'Continue with Free'
                    : 'Next'}
              </MDBBtn>
            </MDBModalFooter>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
      <MessageBoxComponent />
    </>
  );
}

export default SubscriptionPlansModal;
